/** Section 4 - the root state shape. Extending it requires an ADR. */

import type {
  ArmyId, ArtifactDefId, BuildingDefId, CaravanId, CreatureDefId, FactionId, HeroDefId,
  HeroId, IdKind, InteractionId, ObjectId, PlayerId, SkillDefId, SpellDefId, SubskillDefId,
  TownId,
} from "./ids.js";
import type { Modifier } from "./modifiers.js";
import type { RngStreamState } from "./rng/pcg32.js";
import type { RngStream } from "./rng/streams.js";
import type { BattleState } from "./battle/state.js";

export type EntityMap<K extends string, V> = Readonly<Record<K, V>>;

// ---------------------------------------------------------------- resources

export const RESOURCES = ["gold", "wood", "ore", "mercury", "sulfur", "crystal", "gems"] as const;
export type ResourceKind = (typeof RESOURCES)[number];
export type Resources = Readonly<Record<ResourceKind, number>>;

export const ZERO_RESOURCES: Resources = Object.freeze({
  gold: 0, wood: 0, ore: 0, mercury: 0, sulfur: 0, crystal: 0, gems: 0,
});

// ---------------------------------------------------------------- terrain

/**
 * **The index is the save format.** `AdventureMapState.terrain` is a
 * `Uint8Array` of positions in this list, so a terrain may only ever be
 * APPENDED: inserting or reordering re-reads every stored map as a different
 * one and changes every state hash. `highlands` and `wasteland` are therefore
 * at the end rather than beside the grasslands and badlands they belong with,
 * and the paint order that actually matters is `TerrainDef.priority`, which is
 * content and is free to interleave them.
 */
export const TERRAINS = [
  "water", "sand", "dirt", "grass", "snow", "swamp", "rough", "cavern", "lava", "rock",
  "highlands", "wasteland",
] as const;
export type TerrainKind = (typeof TERRAINS)[number];

export const ROAD_KINDS = ["none", "dirt", "gravel", "cobble"] as const;
export type RoadKind = (typeof ROAD_KINDS)[number];

export const RIVER_KINDS = ["none", "water", "ice", "lava", "mud"] as const;
export type RiverKind = (typeof RIVER_KINDS)[number];

// ---------------------------------------------------------------- army

/** A stack in an adventure-side army: a creature type and a count. */
export interface ArmySlot {
  readonly def: CreatureDefId;
  readonly count: number;
}

/** Seven slots; `null` is an empty slot, and slot order is meaningful to the player. */
export interface ArmyState {
  readonly id: ArmyId;
  readonly owner: PlayerId | null;
  readonly slots: readonly (ArmySlot | null)[];
}

// ---------------------------------------------------------------- hero

export type PrimarySkill = "attack" | "defense" | "spellPower" | "knowledge";
export type PrimarySkills = Readonly<Record<PrimarySkill, number>>;

export type SkillLevel = 1 | 2 | 3;

export interface SecondarySkillState {
  readonly def: SkillDefId;
  readonly level: SkillLevel;
  /**
   * Subskills chosen on the way up, in the order they were picked.
   *
   * Optional for the same reason `heldArmies` and `researchedThisTurn` are: it
   * arrived after `save-v5.json` was frozen, and a hero saved before subskills
   * existed has no key rather than an empty array. A rung is settled when this
   * list names one of its choices, so the shape carries no separate counter to
   * fall out of step with it.
   */
  readonly picks?: readonly SubskillDefId[];
}

export interface HeroState {
  readonly id: HeroId;
  readonly def: HeroDefId;
  readonly owner: PlayerId;
  readonly faction: FactionId;

  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** 0..7, clockwise from north; the renderer picks the sprite from this. */
  readonly facing: number;

  readonly level: number;
  readonly experience: number;
  readonly primary: PrimarySkills;
  readonly secondary: readonly SecondarySkillState[];

  readonly movementLeft: number;
  readonly mana: number;

  readonly army: ArmyId;
  readonly artifacts: Readonly<Record<string, ArtifactDefId | null>>;
  readonly backpack: readonly ArtifactDefId[];
  readonly spellbook: readonly SpellDefId[];
  readonly modifiers: readonly Modifier[];

  /** Set while the hero is aboard a boat object. */
  readonly boat: ObjectId | null;
  readonly inTown: TownId | null;
  /** Objects already visited this week or permanently, by object id. */
  readonly visited: readonly ObjectId[];
}

// ---------------------------------------------------------------- town

export interface TownState {
  readonly id: TownId;
  readonly faction: FactionId;
  readonly owner: PlayerId | null;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;

  readonly built: readonly BuildingDefId[];
  /** Set when a building was raised this turn; a town builds once per day. */
  readonly builtThisTurn: boolean;
  /** When true the town builds one affordable structure at the next day boundary. */
  readonly autoBuild?: boolean;
  /** Creatures waiting in the dwellings, by creature def. */
  readonly available: Readonly<Record<string, number>>;
  readonly garrison: ArmyId;
  /** The hero standing in the town, if any. */
  readonly visitingHero: HeroId | null;
  readonly garrisonHero: HeroId | null;
  /** Spells in the mage guild, outer array indexed by guild level - 1. */
  readonly guildSpells: readonly (readonly SpellDefId[])[];
  /**
   * The expansion spell research is one per town per day, in the same shape as
   * `builtThisTurn` and cleared beside it. Optional because it arrived after
   * the save format froze: absent is absent (I6), and reads as "not yet".
   */
  readonly researchedThisTurn?: boolean;
  readonly modifiers: readonly Modifier[];
}

// ---------------------------------------------------------------- map objects

export type MapObjectKind =
  | "resource" | "mine" | "dwelling" | "artifact" | "monster" | "treasure"
  | "scholar" | "shrine" | "boat" | "garrison" | "portal" | "bank"
  | "bonus" | "obstacle" | "sign" | "town" | "hero" | "keymaster" | "gate";

export interface MapObjectState {
  readonly id: ObjectId;
  readonly kind: MapObjectKind;
  /** Content definition id; the shape of `payload` follows from it. */
  readonly def: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly owner: PlayerId | null;
  /** Object-specific plain data (amount left, guard army, flags). */
  readonly payload: Readonly<Record<string, number | string | boolean | null>>;
  /** Guarding army, for banks, artifacts and mines that fight back. */
  readonly guard: ArmyId | null;
  readonly visitedBy: readonly HeroId[];
  readonly removed: boolean;
}

// ---------------------------------------------------------------- map

/**
 * Dense typed arrays, one entry per cell, indexed `z * w * h + y * w + x`.
 * Kept flat rather than chunked until a measurement says otherwise (I16).
 */
export interface AdventureMapState {
  readonly width: number;
  readonly height: number;
  readonly levels: number;
  /** TerrainKind index into TERRAINS. */
  readonly terrain: Uint8Array;
  /** Positional variant selector, 0..255; makes identical terrain non-repeating. */
  readonly variant: Uint8Array;
  /** RoadKind index into ROAD_KINDS. */
  readonly road: Uint8Array;
  /** RiverKind index into RIVER_KINDS. */
  readonly river: Uint8Array;
  /** Movement cost in movement points; 0 means impassable. */
  readonly moveCost: Uint16Array;
  /** ObjectId slot occupying a cell, 0 for none; index into `objectIndex`. */
  readonly occupant: Uint16Array;
  readonly objectIndex: readonly (ObjectId | null)[];
  /**
   * Section 31 - magic ground. 0 is ordinary ground; anything else indexes
   * `groundIndex`.
   *
   * A slot and a legend, the way `occupant` and `objectIndex` already work,
   * rather than an index into the content table: the table is sorted by id and
   * grows, so a number that meant Hexed Soil in one bundle would mean Clover
   * Field in the next and every old save would change what it was standing on.
   * The legend travels with the map and says what its own numbers mean.
   */
  readonly ground: Uint8Array;
  readonly groundIndex: readonly (string | null)[];
}

export type FogVisibility = 0 | 1 | 2; // 0 unseen, 1 remembered, 2 visible

export interface FogState {
  readonly seen: Uint8Array;
  /** Last-seen snapshots of enemy assets, keyed by entity id. Stale by design. */
  readonly memory: Readonly<Record<string, Readonly<Record<string, number | string | null>>>>;
}

// ---------------------------------------------------------------- player

export type PlayerKind = "human" | "ai" | "none";

export interface PlayerState {
  readonly id: PlayerId;
  readonly kind: PlayerKind;
  readonly colour: string;
  readonly faction: FactionId;
  readonly resources: Resources;
  readonly heroes: readonly HeroId[];
  readonly towns: readonly TownId[];
  readonly defeated: boolean;
  /**
   * Days this player has held no town, counted at the day boundary and reset to
   * zero the moment he holds one again. The classic does not put a player out the
   * instant his last town falls: he has seven days to take another one and is
   * only eliminated on the eighth. `0` therefore reads as both "holds a town"
   * and "lost it today", which are the same position - the counter only becomes
   * interesting once a day boundary has passed over it.
   *
   * Stored rather than derived because history is not in state (Section 4): a
   * derived answer would have to guess which day the last town fell.
   */
  readonly townlessDays: number;
  /** Heroes offered in the tavern this week. */
  readonly tavern: readonly HeroDefId[];
  /**
   * Armies a surrendered hero left behind, by hero def, waiting for the
   * re-hire that hands them back. Optional: a save from before the field is
   * a kingdom holding none, and the key is dropped again the moment it is
   * spent, so a state that has never surrendered never carries it.
   */
  readonly heldArmies?: Readonly<Record<string, ArmyId>>;
  readonly modifiers: readonly Modifier[];
}

// ---------------------------------------------------------------- turn

export interface TurnState {
  readonly day: number;
  readonly week: number;
  readonly month: number;
  readonly activePlayer: PlayerId;
  readonly order: readonly PlayerId[];
}

// ---------------------------------------------------------------- pending

export type InteractionPrompt =
  | { readonly kind: "CONFIRM"; readonly text: string }
  | { readonly kind: "CHOOSE"; readonly text: string; readonly options: readonly string[] }
  | { readonly kind: "FIGHT_OR_FLEE"; readonly objectId: ObjectId; readonly threat: number }
  | { readonly kind: "LEVEL_UP"; readonly heroId: HeroId; readonly choices: readonly string[] }
  /** The branch a skill opened on reaching advanced or expert. */
  | {
    readonly kind: "PICK_SUBSKILL";
    readonly heroId: HeroId;
    readonly skill: SkillDefId;
    readonly choices: readonly SubskillDefId[];
  }
  | { readonly kind: "PICK_SPELL"; readonly choices: readonly SpellDefId[] }
  | { readonly kind: "MESSAGE"; readonly text: string };

export type InteractionResponse =
  | { readonly kind: "OK" }
  | { readonly kind: "CANCEL" }
  | { readonly kind: "OPTION"; readonly index: number };

export interface ResumeToken {
  readonly kind: string;
  /** Plain data only - never a closure (Section 3.8). */
  readonly data: Readonly<Record<string, number | string | boolean | null | readonly number[]>>;
}

export interface PendingInteraction {
  readonly id: InteractionId;
  readonly playerId: PlayerId;
  readonly prompt: InteractionPrompt;
  readonly legalResponses: readonly InteractionResponse[];
  readonly resumeToken: ResumeToken;
}

// ------------------------------------------------------------- caravans

/**
 * Creatures walking to a town without a hero (H4 and H5's caravans).
 *
 * The busywork this exists to remove is the hero taxi: in the classic only way a
 * creature bought at an external dwelling reaches the front is for a hero to
 * walk there, pick it up and walk back, which costs a week of somebody's
 * movement and is a decision nobody enjoys making. A caravan is the same
 * creatures making the same journey with nobody driving them.
 *
 * What keeps it from being free reinforcement:
 *
 *  - it is paid for in full when it sets out, at the source's own price;
 *  - it walks at a fixed rate, so distance is still what the map charges;
 *  - it goes to ONE named town, and if that town is not still its owner's when
 *    it arrives, the column is lost. A caravan is not a homing missile.
 *
 * `arrivesOnDay` is absolute rather than a countdown for the reason every other
 * absolute date in this state is: a save loaded on day 40 has to arrive on the
 * day it was always going to arrive on, and a countdown ticked by a loop is one
 * missed tick away from never arriving at all.
 */
export interface CaravanState {
  readonly id: CaravanId;
  readonly owner: PlayerId;
  /** Where it set out from, for the report and for the minimap. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly to: TownId;
  readonly def: CreatureDefId;
  readonly count: number;
  readonly arrivesOnDay: number;
}

// ---------------------------------------------------------------- root

export interface GameState {
  readonly version: number;
  readonly formatVersion: number;
  readonly contentVersion: string;
  readonly rootSeed: number;

  readonly rng: Readonly<Record<RngStream, RngStreamState>>;
  readonly counters: Readonly<Record<IdKind, number>>;

  readonly turn: TurnState;
  readonly players: EntityMap<PlayerId, PlayerState>;
  readonly heroes: EntityMap<HeroId, HeroState>;
  readonly armies: EntityMap<ArmyId, ArmyState>;
  readonly towns: EntityMap<TownId, TownState>;
  readonly objects: EntityMap<ObjectId, MapObjectState>;
  readonly caravans: EntityMap<CaravanId, CaravanState>;
  readonly map: AdventureMapState;
  readonly fog: Readonly<Record<PlayerId, FogState>>;

  readonly battle: BattleState | null;
  readonly pending: PendingInteraction | null;

  readonly victory: {
    readonly condition: string;
    readonly winner: PlayerId | null;
    readonly over: boolean;
  };
}

export const CURRENT_FORMAT_VERSION = 8;

/** Flat index into the dense map arrays. */
export const cellIndex = (m: { width: number; height: number }, x: number, y: number, z: number): number =>
  z * m.width * m.height + y * m.width + x;

export const inBounds = (m: AdventureMapState, x: number, y: number, z: number): boolean =>
  x >= 0 && y >= 0 && z >= 0 && x < m.width && y < m.height && z < m.levels;
